import { useState, useCallback } from 'react';
import { HexColorPicker } from 'react-colorful';
import { useTranslation } from '@parentpath/i18n';
import { useTheme, DEFAULT_COLORS } from '@/contexts/ThemeContext';
import { Loader2, RotateCcw, Check, Palette } from 'lucide-react';

// Convert "R G B" string to "#RRGGBB" hex
function rgbStringToHex(rgb: string): string {
  const parts = rgb.trim().split(/\s+/).map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return '#000000';
  return '#' + parts.map(n => n.toString(16).padStart(2, '0')).join('');
}

// Convert "#RRGGBB" hex to "R G B" string
function hexToRgbString(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r} ${g} ${b}`;
}

type ColorKey = 'orange' | 'teal' | 'dark';

interface ColorEntry {
  key: ColorKey;
  labelKey: string;
}

const COLOR_ENTRIES: ColorEntry[] = [
  { key: 'orange', labelKey: 'profile:customColors.accent' },
  { key: 'teal', labelKey: 'profile:customColors.secondary' },
  { key: 'dark', labelKey: 'profile:customColors.background' },
];

export function ColorPickerSection() {
  const { t } = useTranslation(['profile']);
  const { customColors, setCustomColors, resetColors, applyColors } = useTheme();
  const [activeColor, setActiveColor] = useState<ColorKey | null>(null);
  const [localColors, setLocalColors] = useState<Record<ColorKey, string>>({
    orange: customColors?.orange || DEFAULT_COLORS.orange,
    teal: customColors?.teal || DEFAULT_COLORS.teal,
    dark: customColors?.dark || DEFAULT_COLORS.dark,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleColorChange = useCallback((hex: string) => {
    if (!activeColor) return;
    const rgbStr = hexToRgbString(hex);
    setLocalColors(prev => ({ ...prev, [activeColor]: rgbStr }));
    // Live preview
    applyColors({ ...localColors, [activeColor]: rgbStr });
  }, [activeColor, localColors, applyColors]);

  const handleSave = async () => {
    setIsSaving(true);
    await setCustomColors(localColors);
    setIsSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleReset = async () => {
    setIsSaving(true);
    await resetColors();
    setLocalColors({
      orange: DEFAULT_COLORS.orange,
      teal: DEFAULT_COLORS.teal,
      dark: DEFAULT_COLORS.dark,
    });
    setActiveColor(null);
    setIsSaving(false);
  };

  return (
    <section>
      <h2 className="text-xs font-semibold text-white/70 tracking-wider mb-3 px-1">
        {t('profile:sections.customColors')}
      </h2>
      <div className="profile-card">
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <Palette className="w-5 h-5 text-pp-orange" />
            <div>
              <p className="text-white font-medium text-sm">{t('profile:customColors.title')}</p>
              <p className="text-white/60 text-xs">{t('profile:customColors.description')}</p>
            </div>
          </div>

          {/* Color swatches */}
          <div className="flex gap-3">
            {COLOR_ENTRIES.map(({ key, labelKey }) => (
              <button
                key={key}
                onClick={() => setActiveColor(activeColor === key ? null : key)}
                className={`flex-1 rounded-xl p-3 text-center transition-all ${
                  activeColor === key
                    ? 'ring-2 ring-white ring-offset-2 ring-offset-transparent'
                    : ''
                }`}
                style={{ background: rgbStringToHex(localColors[key]) }}
              >
                <span className="text-white text-xs font-medium drop-shadow-md">
                  {t(labelKey)}
                </span>
              </button>
            ))}
          </div>

          {/* Color picker */}
          {activeColor && (
            <div className="flex justify-center py-2">
              <HexColorPicker
                color={rgbStringToHex(localColors[activeColor])}
                onChange={handleColorChange}
                style={{ width: '100%', maxWidth: '280px' }}
              />
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2 pt-1">
            <button
              onClick={handleReset}
              disabled={isSaving}
              className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white text-sm font-medium hover:bg-white/30 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              {t('profile:customColors.reset')}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white text-sm font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : saved ? (
                <>
                  <Check className="w-4 h-4" />
                  {t('profile:customColors.saved')}
                </>
              ) : (
                t('profile:customColors.save')
              )}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
